// Router-карта приложения. React Router v7 createBrowserRouter + RouterProvider.
//
// Структура (PRD §3.1):
//   /login                                — public
//   /deactivated                          — public (но обычно дёргается после login)
//
//   ProtectedRoute (auth required):
//     /                                  — HomeRedirect на первый доступный tool
//     /tools/banner_generator            — ToolLayout с Segmented + GenerationView
//     /tools/banner_generator/history    — ToolLayout с HistoryView (PRD §3.7)
//     /tools/proofreader                 — ToolLayout (has_history=false → без переключателя)
//     /tools/social_announcer            — ToolLayout с Segmented + SocialAnnouncerPage
//     /tools/social_announcer/history    — ToolLayout с HistoryView
//     /admin/* (requireSuperuser):
//       /admin/users                     — список пользователей + Создать
//       /admin/users/:id/permissions     — per-user permissions
//       /admin/logs                      — логи генераций
//   /*                                    — 404
//
// Маршрут /admin/permissions удалён намеренно — глобальной страницы прав нет,
// права назначаются per-user через /admin/users/{id}/permissions (PRD §3.10).
//
// Code-splitting: tool/admin страницы загружаются через React.lazy. Это даёт:
//   - login flow быстрее (без загрузки tool-кода);
//   - main bundle <800 KB (gzipped), а большая часть UI-кода перенесена в чанки;
//   - после Этапа 4 bundle вырос > 2 MB до split'а, поэтому splitting обязателен.
import { Suspense, lazy } from "react";
import { createBrowserRouter, Navigate } from "react-router-dom";
import { Spin } from "antd";
import { LazyErrorBoundary } from "@/components/LazyErrorBoundary";
import { ProtectedRoute } from "@/components/Layout/ProtectedRoute";
import { AppLayout } from "@/components/Layout/AppLayout";
import { ToolLayout } from "@/components/ToolLayout/ToolLayout";
import { LoginPage } from "@/pages/LoginPage";
import { DeactivatedPage } from "@/pages/DeactivatedPage";
import { NotFoundPage } from "@/pages/NotFoundPage";
import { HomeRedirect } from "@/pages/HomeRedirect";

// Lazy chunks — каждая страница в свой чанк.
const BannerGeneratorPage = lazy(() =>
  import("@/pages/tools/BannerGenerator").then((m) => ({
    default: m.BannerGeneratorPage,
  })),
);
const HistoryView = lazy(() =>
  import("@/pages/tools/History").then((m) => ({ default: m.HistoryView })),
);
const ProofreaderPage = lazy(() =>
  import("@/pages/tools/Proofreader").then((m) => ({
    default: m.ProofreaderPage,
  })),
);
const PublicationCoverPage = lazy(() =>
  import("@/pages/tools/PublicationCover").then((m) => ({
    default: m.PublicationCoverPage,
  })),
);
const SocialAnnouncerPage = lazy(() =>
  import("@/pages/tools/SocialAnnouncer").then((m) => ({
    default: m.SocialAnnouncerPage,
  })),
);
const UsersPage = lazy(() =>
  import("@/pages/admin/UsersPage").then((m) => ({ default: m.UsersPage })),
);
const PermissionsPage = lazy(() =>
  import("@/pages/admin/PermissionsPage").then((m) => ({
    default: m.PermissionsPage,
  })),
);
const LogsPage = lazy(() =>
  import("@/pages/admin/LogsPage").then((m) => ({ default: m.LogsPage })),
);
const ComplaintsPage = lazy(() =>
  import("@/pages/admin/ComplaintsPage").then((m) => ({ default: m.ComplaintsPage })),
);

function LazyFallback() {
  return (
    <div style={{ textAlign: "center", padding: 48 }}>
      <Spin />
    </div>
  );
}

function wrap(node: React.ReactNode) {
  return (
    <LazyErrorBoundary>
      <Suspense fallback={<LazyFallback />}>{node}</Suspense>
    </LazyErrorBoundary>
  );
}

export const router = createBrowserRouter([
  { path: "/login", element: <LoginPage /> },
  { path: "/deactivated", element: <DeactivatedPage /> },
  {
    element: <ProtectedRoute render={(me) => <AppLayout me={me} />} />,
    children: [
      { index: true, element: <HomeRedirect /> },
      {
        path: "tools/banner_generator",
        element: (
          <ToolLayout
            toolSlug="banner_generator"
            toolName="Генератор баннеров"
            hasHistory
          />
        ),
        children: [
          { index: true, element: wrap(<BannerGeneratorPage />) },
          {
            path: "history",
            element: wrap(<HistoryView toolSlug="banner_generator" />),
          },
        ],
      },
      {
        path: "tools/proofreader",
        element: (
          <ToolLayout
            toolSlug="proofreader"
            toolName="Корректура"
            hasHistory
          />
        ),
        children: [
          { index: true, element: wrap(<ProofreaderPage />) },
          {
            path: "history",
            element: wrap(<HistoryView toolSlug="proofreader" />),
          },
        ],
      },
      {
        path: "tools/publication_cover",
        element: (
          <ToolLayout
            toolSlug="publication_cover"
            toolName="Генератор обложек публикаций"
            hasHistory
          />
        ),
        children: [
          { index: true, element: wrap(<PublicationCoverPage />) },
          {
            path: "history",
            element: wrap(<HistoryView toolSlug="publication_cover" />),
          },
        ],
      },
      {
        path: "tools/social_announcer",
        element: (
          <ToolLayout
            toolSlug="social_announcer"
            toolName="Анонсы для соцсетей"
            hasHistory
          />
        ),
        children: [
          { index: true, element: wrap(<SocialAnnouncerPage />) },
          {
            path: "history",
            element: wrap(<HistoryView toolSlug="social_announcer" />),
          },
        ],
      },
      {
        element: <ProtectedRoute requireSuperuser />,
        children: [
          { path: "admin", element: <Navigate to="/admin/users" replace /> },
          { path: "admin/users", element: wrap(<UsersPage />) },
          {
            path: "admin/users/:id/permissions",
            element: wrap(<PermissionsPage />),
          },
          { path: "admin/logs", element: wrap(<LogsPage />) },
          { path: "admin/complaints", element: wrap(<ComplaintsPage />) },
        ],
      },
    ],
  },
  { path: "*", element: <NotFoundPage /> },
]);
